/**
 * SEO_BRIEF_11 section 2.1: the decisions behind /video-compressor and /mp4-converter, kept apart
 * from the encoding so Node can test them. The worker (web/static/burn/src/video-out-worker.ts)
 * asks the browser what it can encode and hands the answers here; the page shows the estimate
 * this file makes before anything is encoded.
 *
 * THE COMPRESSOR. Three modes. "Smaller" and "Much smaller" aim at a share of the source's own
 * bitrate (its size over its length), at the resolution the visitor picked. "Fit under a size"
 * works the bitrate out from the size and the length: the size, less what the MP4 itself takes,
 * less a margin for an encoder that overshoots, over the length, less the sound. Where that
 * leaves too few bits for a clean picture at the size picked, it steps down to 720p, then 480p.
 * Where even 480p at the lowest bitrate this file allows is over the size, the plan says so
 * (`reachable: false`) and keeps the smallest result it can make, which the page offers anyway.
 *
 * THE CONVERTER. A video already in H.264 with AAC sound is repackaged as MP4 with nothing
 * re-encoded. Otherwise only what is not H.264 or AAC is re-encoded: the picture to H.264, the
 * sound to AAC. Sound this browser cannot turn into AAC is kept as it is when MP4 can hold it,
 * and dropped (the page says so) when it cannot.
 *
 * Sizes are bytes; a size the visitor types is in MB of 1,000,000 bytes, the smaller of the two
 * readings, so a file made to fit "25 MB" fits whichever one a mail service means.
 */

export type CompressMode = 'smaller' | 'much-smaller' | 'fit';
export type Resolution = 'keep' | '720' | '480';

export const COMPRESS_MODES: readonly CompressMode[] = ['smaller', 'much-smaller', 'fit'];
export const RESOLUTIONS: readonly Resolution[] = ['keep', '720', '480'];

/** The share of the source's bitrate each mode aims at. */
export const MODE_SHARE: Readonly<Record<Exclude<CompressMode, 'fit'>, number>> = {
  smaller: 0.5,
  'much-smaller': 0.25,
};

/** The sound's bitrate in each mode, in bits per second, when it is re-encoded as AAC. */
export const MODE_AUDIO: Readonly<Record<Exclude<CompressMode, 'fit'>, number>> = {
  smaller: 128_000,
  'much-smaller': 96_000,
};

/** The one-tap size: what most mail services take as an attachment. */
export const EMAIL_MB = 25;
export const MB = 1_000_000;

/**
 * Bits per pixel per frame. Below CLEAN_BPP the picture at that size starts to break up, so "Fit
 * under a size" steps down a resolution; below FLOOR_BPP at the smallest size there is nothing
 * worth keeping, so the plan stops there and says the size can't be reached.
 */
export const CLEAN_BPP = 0.03;
export const FLOOR_BPP = 0.01;

/** Aim this far under the size, for an encoder that overshoots its bitrate. */
export const SAFETY = 0.9;

/** What the MP4 itself takes beyond the media: a header, and an index entry per frame. */
export function containerOverhead(durationS: number): number {
  return 16_000 + Math.max(0, durationS) * 1_000;
}

/** H.264 at 4:2:0 needs even dimensions; an odd one is rounded up. */
export function even(n: number): number {
  const r = Math.max(2, Math.round(n));
  return r % 2 === 0 ? r : r + 1;
}

/** The frame rate the bit budget is reckoned at: the video's own, held between 24 and 30. */
export function budgetFps(fps: number | null): number {
  if (fps === null || !Number.isFinite(fps) || fps <= 0) return 30;
  return Math.min(30, Math.max(24, fps));
}

export interface Size {
  readonly width: number;
  readonly height: number;
}

/**
 * The output size for a resolution choice: the short side at most 720 or 480 pixels, so an
 * upright phone video's 720p is 720 wide. Never larger than the video; always even.
 */
export function scaledSize(source: Size, resolution: Resolution): Size {
  const short = Math.min(source.width, source.height);
  const limit = resolution === 'keep' ? short : Number(resolution);
  const scale = short > limit ? limit / short : 1;
  return { width: even(source.width * scale), height: even(source.height * scale) };
}

/** The bitrate at `bpp` for a frame of this size. */
export function bitrateAt(size: Size, bpp: number, fps: number | null): number {
  return Math.round(size.width * size.height * budgetFps(fps) * bpp);
}

/** What the page knows about a video once the worker has opened it. */
export interface VideoFacts {
  /** The upright picture size (rotation applied). */
  readonly width: number;
  readonly height: number;
  readonly durationS: number;
  readonly bytes: number;
  readonly fps: number | null;
  readonly hasAudio: boolean;
  /** The sound's bitrate when it is kept as it is, if the file says. */
  readonly audioBitrate: number | null;
}

/** What this browser can do with it. */
export interface EncodeAbility {
  /** H.264 can be encoded at each of these sizes. */
  readonly sizes: Readonly<Record<Resolution, boolean>>;
  /** The sound can be decoded and encoded as AAC. */
  readonly aac: boolean;
}

export interface CompressRequest {
  readonly mode: CompressMode;
  readonly resolution: Resolution;
  /** Fit only: the size to come in under, in bytes. */
  readonly targetBytes?: number;
}

export interface CompressPlan {
  readonly width: number;
  readonly height: number;
  /** The resolution the plan settled on: the one asked for, or a lower one for a size to fit. */
  readonly resolution: Resolution;
  readonly videoBitrate: number;
  /** `aac`: re-encoded at `audioBitrate`; `copy`: kept as it is; `none`: no sound. */
  readonly audio: 'aac' | 'copy' | 'none';
  readonly audioBitrate: number;
  readonly estimatedBytes: number;
  /** Fit only: false when even the smallest result is over the size asked for. */
  readonly reachable: boolean;
  /** Fit only: the video is already at or under the size, so there is nothing to do. */
  readonly alreadyFits: boolean;
}

/** The resolutions to try, from the one asked for down, that this browser can encode. */
export function candidates(
  facts: Size,
  asked: Resolution,
  sizes: EncodeAbility['sizes'],
): Resolution[] {
  const start = RESOLUTIONS.indexOf(asked);
  const short = Math.min(facts.width, facts.height);
  const out: Resolution[] = [];
  for (const r of RESOLUTIONS.slice(start)) {
    // 720p of a 480p video is the video's own size: listed once, as 'keep' or the first asked.
    if (r !== 'keep' && Number(r) >= short && out.length > 0) continue;
    if (sizes[r]) out.push(r);
  }
  return out;
}

/** The lowest AAC rate the plan asks for, where the sound's own is lower still. */
export const AAC_FLOOR = 64_000;

function audioFor(
  facts: VideoFacts,
  ability: EncodeAbility,
  aacRate: number,
): { audio: CompressPlan['audio']; audioBitrate: number } {
  if (!facts.hasAudio) return { audio: 'none', audioBitrate: 0 };
  if (ability.aac) {
    // Never above the sound's own bitrate, which would only add bytes: a 96 kbit/s voice track
    // stays at 96, and the bits it leaves go to the picture.
    const own = facts.audioBitrate;
    const cap = own === null ? aacRate : Math.max(AAC_FLOOR, Math.round(own / 1000) * 1000);
    return { audio: 'aac', audioBitrate: Math.min(aacRate, cap) };
  }
  return { audio: 'copy', audioBitrate: facts.audioBitrate ?? 128_000 };
}

function estimate(videoBitrate: number, audioBitrate: number, durationS: number): number {
  return Math.round(((videoBitrate + audioBitrate) * durationS) / 8 + containerOverhead(durationS));
}

/**
 * The plan for one compression, or null when this browser can encode H.264 at none of the sizes
 * (the page shows the "can't make a video at this size" sentence).
 */
export function planCompression(
  facts: VideoFacts,
  ability: EncodeAbility,
  request: CompressRequest,
): CompressPlan | null {
  const sizes = candidates(facts, request.resolution, ability.sizes);
  const first = sizes[0];
  const last = sizes[sizes.length - 1];
  if (first === undefined || last === undefined) return null;
  const duration = Math.max(0.1, facts.durationS);

  if (request.mode !== 'fit') {
    const size = scaledSize(facts, first);
    const { audio, audioBitrate } = audioFor(facts, ability, MODE_AUDIO[request.mode]);
    const sourceRate = (facts.bytes * 8) / duration;
    const floor = bitrateAt(size, FLOOR_BPP, facts.fps);
    const videoBitrate = Math.max(
      floor,
      Math.round(sourceRate * MODE_SHARE[request.mode] - audioBitrate),
    );
    return {
      ...size,
      resolution: first,
      videoBitrate,
      audio,
      audioBitrate,
      estimatedBytes: estimate(videoBitrate, audioBitrate, duration),
      reachable: true,
      alreadyFits: false,
    };
  }

  const target = Math.max(0, request.targetBytes ?? EMAIL_MB * MB);
  const budget = (target * SAFETY - containerOverhead(duration)) * 8;
  const totalRate = budget / duration;
  // The sound gives up bits before the picture does: 128 kbps while the budget is roomy.
  const aacRate = totalRate >= 1_000_000 ? 128_000 : totalRate >= 400_000 ? 96_000 : 64_000;
  const { audio, audioBitrate } = audioFor(facts, ability, aacRate);
  const videoRate = Math.round(totalRate - audioBitrate);
  const alreadyFits = facts.bytes <= target;

  for (const r of sizes) {
    const size = scaledSize(facts, r);
    if (videoRate >= bitrateAt(size, CLEAN_BPP, facts.fps)) {
      return {
        ...size,
        resolution: r,
        videoBitrate: videoRate,
        audio,
        audioBitrate,
        estimatedBytes: estimate(videoRate, audioBitrate, duration),
        reachable: true,
        alreadyFits,
      };
    }
  }
  // Below a clean picture at every size: the smallest size, down to the floor.
  const size = scaledSize(facts, last);
  const floor = bitrateAt(size, FLOOR_BPP, facts.fps);
  const videoBitrate = Math.max(floor, videoRate);
  return {
    ...size,
    resolution: last,
    videoBitrate,
    audio,
    audioBitrate,
    estimatedBytes: estimate(videoBitrate, audioBitrate, duration),
    reachable: videoRate >= floor,
    alreadyFits,
  };
}

/** A size the visitor typed, in MB, as bytes; null when it is not a usable number. */
export function targetFromMb(text: string): number | null {
  const value = Number(String(text).trim().replace(',', '.'));
  if (!Number.isFinite(value) || value <= 0 || value > 100_000) return null;
  return Math.round(value * MB);
}

/* ── the converter ──────────────────────────────────────────────────────── */

/** The sound codecs an MP4 carries well enough to keep when they can't become AAC here. */
export const MP4_KEEPS: ReadonlySet<string> = new Set(['mp3', 'opus', 'ac3', 'eac3', 'flac']);

export interface StreamFacts {
  /** The picture's codec as mediabunny names it: `avc`, `hevc`, `vp9`, `vp8`, `av1`… */
  readonly videoCodec: string | null;
  /** The sound's codec, or null for a silent video. */
  readonly audioCodec: string | null;
  /** This browser can decode the sound and encode AAC. */
  readonly aac: boolean;
}

export interface ConvertPlan {
  readonly video: 'copy' | 'encode';
  /** `copy`: AAC as it is; `aac`: re-encoded; `keep`: another codec MP4 holds; `drop`: lost. */
  readonly audio: 'copy' | 'aac' | 'keep' | 'drop' | 'none';
  /**
   * Nothing is re-encoded and nothing is lost: the picture is copied, and so is the sound, as
   * AAC or as the codec it came in (an AC-3 or MP3 kept as it is). The page then says "Nothing
   * was re-encoded.", and the copy promises the picture and sound are exactly as they were, so a
   * sound turned into AAC, or dropped, is not this.
   */
  readonly remux: boolean;
}

export function planConversion(facts: StreamFacts): ConvertPlan {
  const video = facts.videoCodec === 'avc' ? 'copy' : 'encode';
  let audio: ConvertPlan['audio'];
  if (facts.audioCodec === null) audio = 'none';
  else if (facts.audioCodec === 'aac') audio = 'copy';
  else if (facts.aac) audio = 'aac';
  else if (MP4_KEEPS.has(facts.audioCodec)) audio = 'keep';
  else audio = 'drop';
  const asItWas = audio === 'copy' || audio === 'keep' || audio === 'none';
  return { video, audio, remux: video === 'copy' && asItWas };
}

/* ── names ──────────────────────────────────────────────────────────────── */

function baseName(name: string): string {
  return (
    name
      .replace(/\.[^./\\]+$/, '')
      .replace(/[\\/:*?"<>|]+/g, '_')
      .trim() || 'video'
  );
}

/** "holiday.mov" -> "holiday-compressed.mp4". */
export function compressedName(name: string): string {
  return `${baseName(name)}-compressed.mp4`;
}

/** "holiday.mov" -> "holiday.mp4"; an MP4 already keeps its name apart: "clip-converted.mp4". */
export function convertedName(name: string): string {
  const base = baseName(name);
  return /\.mp4$/i.test(name) ? `${base}-converted.mp4` : `${base}.mp4`;
}

/** How much smaller, as a whole percent of the original; 0 when it isn't smaller at all. */
export function savedPercent(before: number, after: number): number {
  if (!(before > 0) || after >= before) return 0;
  return Math.round((1 - after / before) * 100);
}
